import { useMutation, useQuery } from "@apollo/client";
import { pick, sortBy } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBeforeunload } from "react-beforeunload";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  AssessmentGroupListFragmentFragment,
  ComplianceProgramAppliesTo,
  ComplianceProgramCalibrationType,
  ComplianceProgramRecurrence,
  ComplianceProgramState,
  ComplianceProgramStep,
  GetAssessmentGroupsListQuery,
  GetAssessmentGroupsListQueryVariables,
  GetComplianceProgramEditQuery,
  GetComplianceProgramEditQueryVariables,
  SaveComplianceProgramMutation,
  SaveComplianceProgramMutationVariables,
} from "types/graphql-schema";
import { FormError } from "types/topicflow";

import getAssessmentGroupsListQuery from "@apps/assessments/graphql/get-assessment-groups-list-query";
import createOrUpdateComplianceProgramMutation from "@apps/programs/graphql/create-or-update-compliance-program-mutation";
import getComplianceProgramEditQuery from "@apps/programs/graphql/get-compliance-program-edit-query";
import getComplianceProgramsQuery from "@apps/programs/graphql/get-compliance-programs-query";
import { NO_PARTICIPANTS_ERROR } from "@apps/programs/helpers";
import { useProgramsSection } from "@apps/programs/programs-section-context";
import useLabel from "@apps/use-label/use-label";
import {
  currentOrganizationVar,
  currentUserVar,
  isAdminVar,
  successNotificationVar,
} from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Callout from "@components/callout/callout";
import Layout from "@components/layout/layout";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import Tabs from "@components/tabs/tabs";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull } from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";
import useUrlQueryParams from "@helpers/hooks/use-url-query-params";

import {
  ProgramSaveMode,
  convertComplianceProgramToForm,
  convertProgramFormToMutationVariables,
  getDefaultProgramDates,
  getDefaultProgramFormStep,
  isKickoffDatePast,
  validateProgramForm,
} from "./form-data";
import ProgramFormNotifications from "./form-tabs/program-form-notifications";
import ProgramFormParticipants from "./form-tabs/program-form-participants";
import ProgramFormQuestions from "./form-tabs/program-form-questions";
import ProgramFormReadiness from "./form-tabs/program-form-readiness";
import ProgramFormSummary from "./form-tabs/program-form-summary";
import ProgramFormWhoCanStepIn from "./form-tabs/program-form-who-can-step-in";
import ProgramFormWorkflow from "./form-tabs/program-form-workflow";
import ProgramFormErrorBanner from "./program-form-error-banner";
import ProgramFormLeaveDialog from "./program-form-leave-dialog";
import ProgramFormStepBar, {
  ProgramFormBackButton,
  ProgramFormForwardButtons,
} from "./program-form-step-bar";
import {
  ComplianceProgramType,
  ProgramEvaluationPeriodType,
  ProgramForm,
  ProgramFormStep,
  ProgramFormTab,
  programFormTabOrder,
} from "./types";
import useProgramFormSteps, {
  fieldToTab,
  getTabsWithErrors,
} from "./use-program-form-steps";

/** The form as text, with its lists in a fixed order. Editing a step moves it
 * to the end of `steps`, so without this, turning a setting on and back off
 * would look like a change. */
const formSnapshot = (form: ProgramForm) =>
  JSON.stringify({
    ...form,
    steps: sortBy(form.steps, "stepType"),
    appliesToTeams: sortBy(form.appliesToTeams, "id"),
    appliesToManagers: sortBy(form.appliesToManagers, "id"),
    appliesToUsers: sortBy(form.appliesToUsers, "id"),
    excludedUsers: sortBy(form.excludedUsers, "id"),
  });

// The fields that decide who a review applies to. A "nobody matches" error from
// the backend stays on the form only while these are as they were.
const participantRuleKey = (program: ProgramForm) =>
  JSON.stringify(
    pick(program, [
      "ongoing",
      "appliesTo",
      "appliesToTeams",
      "appliesToManagers",
      "appliesToUsers",
      "excludedUsers",
      "excludedHireDateAfter",
    ]),
  );

const getProgramTypeFromUrlParam = (programType?: ComplianceProgramType) => {
  if (programType === ComplianceProgramType.ONE_ON_ONE) {
    return ComplianceProgramType.ONE_ON_ONE;
  }
  if (programType === ComplianceProgramType.ENGAGEMENT_SURVEY) {
    return ComplianceProgramType.ENGAGEMENT_SURVEY;
  }
  return ComplianceProgramType.REVIEW;
};

const ComplianceProgramForm = () => {
  const link = useLink();
  const location = useLocation();
  const label = useLabel();
  const { basePath, listUrl, rootCrumb, lockedProgramType, noun } =
    useProgramsSection();
  const nounLower = noun.toLowerCase();
  const isAdmin = isAdminVar();
  const organization = currentOrganizationVar();
  const currentUser = currentUserVar();

  const { complianceProgramId: complianceProgramIdParam } = useParams<{
    complianceProgramId: string;
  }>();
  const complianceProgramId = parseInt(complianceProgramIdParam);
  const isNew = isNaN(complianceProgramId);
  const isDuplicate = !isNew && location.pathname.includes("/duplicate");
  const { type: programTypeParam } = useUrlQueryParams<{
    type?: string;
  }>({
    type: undefined,
  });

  const [attemptedMode, setAttemptedMode] = useState<ProgramSaveMode | null>(
    null,
  );
  // The step a draft was stopped on for having errors, by Next or by a tab
  // ahead. Only that step's errors show, and only while the admin is on it.
  const [refusedStep, setRefusedStep] = useState<ProgramFormTab | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Only the backend knows who a participant rule matches, so it is the publish
  // that finds out nobody does. Kept with the rule it was about: once the admin
  // changes the rule, the error goes until the next Publish says otherwise.
  const [noParticipantsRuleKey, setNoParticipantsRuleKey] = useState<
    string | null
  >(null);
  // The program type is locked by the section the form is opened from (Reviews
  // or Surveys), falling back to the legacy `?type=` URL param when opened from
  // the generic Programs context. Existing programs derive their type from their
  // templates once loaded (see the edit query's onCompleted below).
  const urlProgramType =
    lockedProgramType ??
    getProgramTypeFromUrlParam(programTypeParam as ComplianceProgramType);
  const [complianceProgramType, setComplianceProgramType] =
    useState<ComplianceProgramType>(urlProgramType);
  const [proposedComplianceProgram, setProposedComplianceProgram] =
    useState<ProgramForm>(() => ({
      organizationId: organization.id,
      title: "",
      ...getDefaultProgramDates(),
      ongoing: false,
      state: ComplianceProgramState.Draft,
      recurrence: ComplianceProgramRecurrence.None,
      appliesTo: isAdmin
        ? ComplianceProgramAppliesTo.Organization
        : ComplianceProgramAppliesTo.CreatorDirectReports,
      appliesToTeams: [],
      appliesToManagers: [],
      appliesToUsers: [],
      excludedUsers: [],
      scheduledReminders: [],
      evaluationPeriod: {
        type: ProgramEvaluationPeriodType.PAST_HALF_YEAR,
      },
      sharesWithSubject: true,
      // Calibration adjusts manager reviews. A survey or a 1-on-1 cycle has none,
      // and its form has no control to turn calibration off.
      calibration:
        urlProgramType === ComplianceProgramType.REVIEW
          ? ComplianceProgramCalibrationType.PostCalibration
          : ComplianceProgramCalibrationType.Off,
      steps:
        urlProgramType === ComplianceProgramType.ENGAGEMENT_SURVEY
          ? [getDefaultProgramFormStep(ComplianceProgramStep.EngagementSurvey)]
          : urlProgramType === ComplianceProgramType.ONE_ON_ONE
            ? [getDefaultProgramFormStep(ComplianceProgramStep.OneOnOne)]
            : [
                getDefaultProgramFormStep(ComplianceProgramStep.PeerReview),
                getDefaultProgramFormStep(ComplianceProgramStep.SelfReview),
                getDefaultProgramFormStep(ComplianceProgramStep.DownwardReview),
                getDefaultProgramFormStep(ComplianceProgramStep.Delivery),
              ],
    }));
  const [availableAssessmentGroups, setAvailableAssessmentGroups] = useState<
    AssessmentGroupListFragmentFragment[]
  >([]);
  const [canUpdateTemplates, setCanUpdateTemplates] = useState(isNew);
  // The form as it was opened, so leaving can warn when that would lose
  // changes. An existing review replaces it once it has loaded.
  const [openedForm, setOpenedForm] = useState(() =>
    formSnapshot(proposedComplianceProgram),
  );
  const hasUnsavedChanges = useMemo(
    () => formSnapshot(proposedComplianceProgram) !== openedForm,
    [proposedComplianceProgram, openedForm],
  );
  // Set just before the form sends the admin away on purpose (after a save,
  // or Discard), so the leave warning stays out of the way.
  const isLeavingRef = useRef(false);
  // Closing or reloading the tab can only get the browser's own warning.
  useBeforeunload((event) => {
    if (hasUnsavedChanges && !isLeavingRef.current) {
      event.preventDefault();
    }
  });
  // Leaving within the app is held back and asked about in our own dialog
  // instead. Moving between steps only changes `?step=`, so it is not leaving.
  // The browser's Back and Forward both count as a "POP", so the address held
  // back is gone to directly rather than by stepping through history.
  const history = useHistory();
  const [pendingLeave, setPendingLeave] = useState<string | null>(null);
  useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }
    return history.block((nextLocation) => {
      if (isLeavingRef.current || nextLocation.pathname === location.pathname) {
        return;
      }
      setPendingLeave(
        `${nextLocation.pathname}${nextLocation.search}${nextLocation.hash}`,
      );
      return false;
    });
  }, [hasUnsavedChanges, history, location.pathname]);

  const noParticipantsError: FormError = useMemo(
    () => ({
      field: "appliesTo",
      message: `Nobody matches who this ${nounLower} applies to. Change it before you publish.`,
    }),
    [nounLower],
  );
  // Saving a draft asks for almost nothing, publishing for the full setup. The
  // errors shown are the ones for whichever button the admin last pressed.
  // Having nobody to send to blocks only Publish, and only while the rule is
  // the one the backend refused.
  const draftErrors = validateProgramForm(proposedComplianceProgram, "draft");
  const hasNoParticipants =
    noParticipantsRuleKey !== null &&
    noParticipantsRuleKey === participantRuleKey(proposedComplianceProgram);
  const publishErrors = [
    ...validateProgramForm(proposedComplianceProgram, "publish"),
    ...(hasNoParticipants ? [noParticipantsError] : []),
  ];
  const displayErrors = attemptedMode === "draft" ? draftErrors : publishErrors;
  // A copy has not been saved anywhere yet, so saving it as it is still makes
  // something. Anything else with no edits has nothing to save or discard.
  const isUnchanged = !isDuplicate && !hasUnsavedChanges;
  // Only a draft gets the "save now, publish later" pair of buttons. Every other state
  // keeps the state it is in: publishing, resuming and reopening belong to the action
  // dropdown, and the mutation refuses any other move.
  const isDraftProgram =
    proposedComplianceProgram.state === ComplianceProgramState.Draft;
  // A draft is set up step by step, and can only be published once every step
  // is done, so nobody publishes without seeing Readiness.
  const isGuidedFlow = isDraftProgram;
  const {
    selectedTab,
    selectTab,
    isTabLocked,
    doneSteps,
    stepsBeforeEndDone,
    completeStep,
    restoreSteps,
    publishBlockedReason,
  } = useProgramFormSteps({
    program: proposedComplianceProgram,
    errors: publishErrors,
    isGuidedFlow,
  });

  const homeUrl = useMemo(
    () =>
      isNew || isDuplicate ? listUrl : `${basePath}/${complianceProgramId}`,
    [complianceProgramId, isNew, isDuplicate, basePath, listUrl],
  );

  const handlePartialProgramFormUpdate = useCallback(
    (data: Partial<ProgramForm>) => {
      setProposedComplianceProgram((prev) => ({
        ...prev,
        ...data,
      }));
    },
    [],
  );

  const [
    createOrUpdateComplianceProgram,
    { loading: isSavingComplianceProgram },
  ] = useMutation<
    SaveComplianceProgramMutation,
    SaveComplianceProgramMutationVariables
  >(createOrUpdateComplianceProgramMutation);

  const { data: complianceProgramData, loading: isLoadingComplianceProgram } =
    useQuery<
      GetComplianceProgramEditQuery,
      GetComplianceProgramEditQueryVariables
    >(getComplianceProgramEditQuery, {
      variables: {
        complianceProgramId: isNew ? -1 : complianceProgramId,
        organizationId: organization.id,
        complianceProgramExists: !isNew,
      },

      onCompleted: (response) => {
        if (isNew) {
          return;
        }

        if (!response.complianceProgram) {
          link.redirect(homeUrl);
        } else {
          const hasEngagementSurveyTemplate =
            !!response.complianceProgram.engagementSurveyTemplate;
          setComplianceProgramType(
            hasEngagementSurveyTemplate
              ? ComplianceProgramType.ENGAGEMENT_SURVEY
              : ComplianceProgramType.REVIEW,
          );

          const savedProgram = convertComplianceProgramToForm(
            organization.id,
            isDuplicate,
            response,
          );
          setProposedComplianceProgram(savedProgram);
          setOpenedForm(formSnapshot(savedProgram));
          // A draft reopens with the steps it had already got through.
          if (savedProgram.state === ComplianceProgramState.Draft) {
            restoreSteps(savedProgram);
          }

          setCanUpdateTemplates(
            isDuplicate ||
              response.complianceProgram.state === ComplianceProgramState.Draft,
          );
        }
      },
    });

  const { loading: isLoadingAvailableAssessmentGroups } = useQuery<
    GetAssessmentGroupsListQuery,
    GetAssessmentGroupsListQueryVariables
  >(getAssessmentGroupsListQuery, {
    variables: {
      organizationId: organization.id,
      archived: canUpdateTemplates ? false : null,
    },
    onCompleted: (response) => {
      const groups = response.assessmentGroups
        ? assertEdgesNonNull(response.assessmentGroups)
        : [];
      setAvailableAssessmentGroups(groups);
    },
  });

  const scrollToFieldError = useCallback((field: string) => {
    const fieldAliases: Record<string, string> = {
      dueDate: "startDate",
      periodEndDate: "periodStartDate",
    };
    const targetField = fieldAliases[field] ?? field;
    // The first error message inside the field, when it marks one. The
    // Questions tab's columns scroll on their own, so the error itself, not
    // the area around them, has to be brought into view.
    const el =
      document.querySelector(`#field-${targetField} [data-field-error]`) ??
      document.getElementById(`field-${targetField}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  const revealErrors = useCallback(
    (errors: FormError[], mode: ProgramSaveMode) => {
      setAttemptedMode(mode);
      if (errors.length === 0) {
        return;
      }
      const firstError = errors[0];
      const targetTab = fieldToTab[firstError.field];
      if (targetTab !== undefined && targetTab !== selectedTab) {
        selectTab(targetTab);
      }
      window.setTimeout(() => scrollToFieldError(firstError.field), 50);
    },
    [scrollToFieldError, selectTab, selectedTab],
  );

  const handleJumpToError = useCallback(
    (error: FormError) => {
      const targetTab = fieldToTab[error.field];
      if (targetTab !== undefined && targetTab !== selectedTab) {
        selectTab(targetTab);
        window.setTimeout(() => scrollToFieldError(error.field), 50);
      } else {
        scrollToFieldError(error.field);
      }
    },
    [scrollToFieldError, selectTab, selectedTab],
  );

  const { ConfirmationDialog: ConfirmKickoffDialog, confirm: confirmKickoff } =
    useConfirm(
      "This will kick off now",
      `The start date has already passed, so publishing sends the kickoff to everyone in this ${nounLower} straight away.`,
    );

  // Which save is running, so only the button that started it says "Saving".
  const [savingState, setSavingState] = useState<ComplianceProgramState | null>(
    null,
  );
  const handleSaveProgram = useCallback(
    // `leaveTo` is where to go once saved, when saving was the admin's answer
    // to leaving the page. Otherwise the save goes back to the list, or to
    // the Progress tab of a review it has just published.
    async (state: ComplianceProgramState, leaveTo?: string) => {
      const mode: ProgramSaveMode =
        state === ComplianceProgramState.Draft ? "draft" : "publish";
      const errors = validateProgramForm(proposedComplianceProgram, mode);
      if (errors.length > 0) {
        revealErrors(errors, mode);
        return;
      }
      if (
        proposedComplianceProgram.state === ComplianceProgramState.Draft &&
        state === ComplianceProgramState.Published &&
        isKickoffDatePast(proposedComplianceProgram) &&
        !(await confirmKickoff())
      ) {
        return;
      }
      setSavingState(state);
      createOrUpdateComplianceProgram({
        variables: {
          ...convertProgramFormToMutationVariables(
            proposedComplianceProgram,
            canUpdateTemplates,
          ),
          state,
        },
        context: {
          errorMatches: [
            {
              // Matches the backend message verbatim. Without a match the raw
              // GraphQL error is what the admin reads, and it says "Program".
              match:
                "Program participants include employees not in your management tree",
              title: `${noun} participants include employees not in your management tree`,
            },
            {
              match: "Can't update templates on a published program",
              title: `This ${nounLower} has already been published. Please refresh the page.`,
            },
            {
              match: "can't be published yet",
              title: `${noun} not published`,
              showMessage: true,
            },
            // Shown as a form error by onError below, not as a toast.
            { match: NO_PARTICIPANTS_ERROR, silent: true },
          ],
        },
        refetchQueries: [getComplianceProgramsQuery],
        // Every other error is a toast from the error link. Passing onError
        // also stops the call rejecting with nobody to catch it.
        onError: (error) => {
          if (
            error.graphQLErrors.some(({ message }) =>
              message.includes(NO_PARTICIPANTS_ERROR),
            )
          ) {
            setNoParticipantsRuleKey(
              participantRuleKey(proposedComplianceProgram),
            );
            revealErrors([noParticipantsError], mode);
          }
        },
        onCompleted: (data) => {
          const isPublishingDraft =
            state === ComplianceProgramState.Published &&
            proposedComplianceProgram.state === ComplianceProgramState.Draft;
          successNotificationVar({
            title:
              state === ComplianceProgramState.Draft
                ? `${noun} saved for later`
                : isPublishingDraft
                  ? `${noun} published`
                  : `${noun} saved`,
          });
          // A review just published opens on its Progress tab, to watch it start.
          const savedId =
            data.createOrUpdateComplianceProgram?.complianceProgram?.id;
          isLeavingRef.current = true;
          link.redirect(
            leaveTo ??
              (isPublishingDraft && savedId
                ? `${basePath}/${savedId}/progress`
                : homeUrl),
          );
        },
      });
    },
    [
      createOrUpdateComplianceProgram,
      basePath,
      homeUrl,
      link,
      proposedComplianceProgram,
      canUpdateTemplates,
      revealErrors,
      confirmKickoff,
      noun,
      nounLower,
      noParticipantsError,
    ],
  );

  const handleDiscardAndLeave = () => {
    if (!pendingLeave) {
      return;
    }
    setPendingLeave(null);
    isLeavingRef.current = true;
    history.push(pendingLeave);
  };

  // A draft that fails its checks stays put and shows what is missing.
  const handleSaveAndLeave = () => {
    if (!pendingLeave) {
      return;
    }
    setPendingLeave(null);
    handleSaveProgram(
      isDraftProgram
        ? ComplianceProgramState.Draft
        : proposedComplianceProgram.state,
      pendingLeave,
    );
  };

  const handleRemoveStep = useCallback(
    (stepType: ComplianceProgramStep) => {
      const linkedSteps: Partial<
        Record<ComplianceProgramStep, ComplianceProgramStep[]>
      > = {
        [ComplianceProgramStep.SelfReview]: [
          ComplianceProgramStep.DownwardReview,
          ComplianceProgramStep.PreCalibration,
          ComplianceProgramStep.PostCalibration,
        ],
        [ComplianceProgramStep.DownwardReview]: [
          ComplianceProgramStep.SelfReview,
          ComplianceProgramStep.PreCalibration,
          ComplianceProgramStep.PostCalibration,
        ],
      };
      const reviewSteps = [
        ComplianceProgramStep.PeerReview,
        ComplianceProgramStep.UpwardReview,
        ComplianceProgramStep.DownwardReview,
      ];
      const newSteps = proposedComplianceProgram.steps.filter(
        (oldStep) =>
          oldStep.stepType !== stepType &&
          (linkedSteps[stepType] === undefined ||
            !linkedSteps[stepType].includes(oldStep.stepType)),
      );
      const remainingReviewStepCount = newSteps.filter((s) =>
        reviewSteps.includes(s.stepType),
      ).length;
      const isPostCalibration =
        !!newSteps.find(
          (s) => s.stepType === ComplianceProgramStep.DownwardReview,
        ) &&
        proposedComplianceProgram.calibration ===
          ComplianceProgramCalibrationType.PostCalibration;
      const isPreCalibration =
        !!newSteps.find(
          (s) => s.stepType === ComplianceProgramStep.DownwardReview,
        ) &&
        proposedComplianceProgram.calibration ===
          ComplianceProgramCalibrationType.PreCalibration;
      const isDelivery = !!newSteps.find(
        (s) => s.stepType === ComplianceProgramStep.Delivery,
      );

      // remove delivery step if no more review steps
      handlePartialProgramFormUpdate({
        calibration: isPreCalibration
          ? ComplianceProgramCalibrationType.PreCalibration
          : isPostCalibration
            ? ComplianceProgramCalibrationType.PostCalibration
            : ComplianceProgramCalibrationType.Off,
        sharesWithSubject: isDelivery && remainingReviewStepCount > 0,
        steps: newSteps.filter(
          (oldStep) =>
            oldStep.stepType !== ComplianceProgramStep.Delivery ||
            remainingReviewStepCount > 0,
        ),
      });
    },
    [
      handlePartialProgramFormUpdate,
      proposedComplianceProgram.steps,
      proposedComplianceProgram.calibration,
    ],
  );

  const handleAddOrUpdateStep = useCallback(
    (newStep: ProgramFormStep) => {
      const existingStep = proposedComplianceProgram.steps.find(
        (s) => s.stepType === newStep.stepType,
      );
      const additionalSteps = [];
      if (
        newStep.stepType === ComplianceProgramStep.SelfReview &&
        !proposedComplianceProgram.steps.find(
          (s) => s.stepType === ComplianceProgramStep.DownwardReview,
        )
      ) {
        additionalSteps.push(
          getDefaultProgramFormStep(ComplianceProgramStep.DownwardReview),
        );
      }
      if (
        newStep.stepType === ComplianceProgramStep.DownwardReview &&
        !proposedComplianceProgram.steps.find(
          (s) => s.stepType === ComplianceProgramStep.SelfReview,
        )
      ) {
        additionalSteps.push(
          getDefaultProgramFormStep(ComplianceProgramStep.SelfReview),
        );
      }

      // remove delivery step if no more review steps
      handlePartialProgramFormUpdate({
        steps: [
          ...proposedComplianceProgram.steps.filter(
            (oldStep) => oldStep.stepType !== newStep.stepType,
          ),
          {
            ...getDefaultProgramFormStep(newStep.stepType),
            ...(existingStep ?? {}),
            ...newStep,
          },
          ...additionalSteps,
        ],
      });
    },
    [handlePartialProgramFormUpdate, proposedComplianceProgram.steps],
  );

  const handleAssessmentGroupCreated = useCallback(
    (assessmentGroup: AssessmentGroupListFragmentFragment) => {
      setAvailableAssessmentGroups((prev) => [
        ...prev.filter(({ id }) => id !== assessmentGroup.id),
        assessmentGroup,
      ]);
    },
    [],
  );
  const handleAssessmentGroupArchived = useCallback(
    (assessmentGroupId: number) => {
      setAvailableAssessmentGroups((prev) =>
        prev.filter(({ id }) => id !== assessmentGroupId),
      );
      handlePartialProgramFormUpdate({
        steps: proposedComplianceProgram.steps.map((step) => {
          if ("questionSetIds" in step) {
            return {
              ...step,
              questionSetIds: step.questionSetIds.filter(
                (id) => id !== assessmentGroupId,
              ),
            };
          }
          return step;
        }),
      });
    },
    [handlePartialProgramFormUpdate, proposedComplianceProgram.steps],
  );

  const isLoading =
    isLoadingComplianceProgram || isLoadingAvailableAssessmentGroups;

  // A published or paused review keeps its questions locked, apart from the changes the
  // Questions tab offers on it: remove, show again and reword (TF-1697). A closed review
  // must be reopened first, and a draft keeps its usual editing.
  const loadedProgram = complianceProgramData?.complianceProgram;
  const publishedReviewId =
    !isNew &&
    !isDuplicate &&
    !canUpdateTemplates &&
    loadedProgram?.canUpdate?.permission &&
    (loadedProgram.state === ComplianceProgramState.Published ||
      loadedProgram.state === ComplianceProgramState.Paused)
      ? loadedProgram.id
      : null;

  const submitAttempted = attemptedMode !== null;
  // Each step is checked against what publishing needs, since that is where
  // the steps lead. The tab markers after a save show that save's errors.
  const tabsWithErrors = getTabsWithErrors(displayErrors);
  const currentStepErrors = publishErrors.filter(
    (error) => fieldToTab[error.field] === selectedTab,
  );
  const visibleErrors = submitAttempted
    ? displayErrors
    : refusedStep === selectedTab
      ? currentStepErrors
      : [];
  // Only a save or publish marks other tabs: a refused step is about itself.
  const showTabErrorMarkers = submitAttempted;

  const moveToStep = (step: ProgramFormTab) => {
    selectTab(step);
    scrollContainerRef.current?.scrollTo({ top: 0 });
  };

  const nextStep =
    programFormTabOrder[programFormTabOrder.indexOf(selectedTab) + 1];

  // Moving forward, by Next or by a tab further on, works like pressing Next
  // once per step: each step on the way is checked and marked done, and the
  // first one with errors stops the move and shows them.
  const goForwardTo = (target: ProgramFormTab) => {
    const steps = programFormTabOrder.slice(
      programFormTabOrder.indexOf(selectedTab),
      programFormTabOrder.indexOf(target),
    );
    for (const step of steps) {
      const stepErrors = publishErrors.filter(
        (error) => fieldToTab[error.field] === step,
      );
      if (stepErrors.length > 0) {
        setRefusedStep(step);
        if (step !== selectedTab) {
          moveToStep(step);
        }
        window.setTimeout(() => scrollToFieldError(stepErrors[0].field), 50);
        return;
      }
      completeStep(step);
    }
    setRefusedStep(null);
    moveToStep(target);
  };

  const handleNextStep = () => {
    if (nextStep !== undefined) {
      goForwardTo(nextStep);
    }
  };

  // The step straight after this one is open too, as another way to press
  // Next. Steps beyond it stay locked until they are reached.
  const lockedTab = (tab: ProgramFormTab) =>
    isTabLocked(tab) && tab !== nextStep
      ? { disabled: true, tooltip: "Finish the steps before this one first" }
      : {};
  const stepNumber = (tab: ProgramFormTab) =>
    isGuidedFlow ? programFormTabOrder.indexOf(tab) + 1 : undefined;
  const handleClickTab = (tab: ProgramFormTab) => {
    const isForward =
      programFormTabOrder.indexOf(tab) >
      programFormTabOrder.indexOf(selectedTab);
    if (isGuidedFlow && isForward) {
      goForwardTo(tab);
    } else {
      selectTab(tab);
    }
  };

  const handlePreviousStep = () => {
    const previousStep =
      programFormTabOrder[programFormTabOrder.indexOf(selectedTab) - 1];
    if (previousStep !== undefined) {
      moveToStep(previousStep);
    }
  };

  const backButton = (
    <ProgramFormBackButton
      currentStep={selectedTab}
      isSaving={isSavingComplianceProgram}
      onBack={handlePreviousStep}
    />
  );
  const forwardButtons = (
    <ProgramFormForwardButtons
      currentStep={selectedTab}
      canJumpToEnd={stepsBeforeEndDone}
      publishBlockedReason={publishBlockedReason}
      isSaving={isSavingComplianceProgram}
      onNext={handleNextStep}
      onJumpToEnd={() => goForwardTo(ProgramFormTab.Readiness)}
      isPublishing={
        isSavingComplianceProgram &&
        savingState === ComplianceProgramState.Published
      }
      onPublish={() => handleSaveProgram(ComplianceProgramState.Published)}
    />
  );
  // A published review has no steps to walk, so its bar only saves or throws
  // away an edit. Both wait until something has been changed.
  const discardButton = (
    <Button
      to={homeUrl}
      onClick={() => {
        // A greyed-out link still gets the click, so only a real Discard
        // counts as leaving.
        if (hasUnsavedChanges && !isSavingComplianceProgram) {
          isLeavingRef.current = true;
        }
      }}
      disabled={!hasUnsavedChanges || isSavingComplianceProgram}
      text="Discard changes"
    />
  );
  const saveChangesButton = (
    <Button
      type="button"
      onClick={() => handleSaveProgram(proposedComplianceProgram.state)}
      tooltip={
        isUnchanged ? "No changes to save" : (publishErrors[0]?.message ?? null)
      }
      disabled={isUnchanged || isSavingComplianceProgram}
      theme={buttonTheme.primary}
      text={isSavingComplianceProgram ? "Saving" : "Save changes"}
    />
  );
  // Saving a draft is a way out of the form, so it is a quiet button beside
  // the ones that move forward rather than a second action at the top.
  const saveDraftButton = (
    <Button
      type="button"
      onClick={() => handleSaveProgram(ComplianceProgramState.Draft)}
      tooltip={
        isUnchanged ? "Nothing to save yet" : (draftErrors[0]?.message ?? null)
      }
      disabled={isUnchanged || isSavingComplianceProgram}
      theme={buttonTheme.text}
      text="Save and exit"
    />
  );
  // A copy starts today (see convertComplianceProgramToForm), so tell the admin
  // which dates it moved from. The query still holds the source review.
  const sourceStartDate = complianceProgramData?.complianceProgram?.startDate;
  const sourceDueDate = complianceProgramData?.complianceProgram?.dueDate;
  const formatSourceDate = (date: string) =>
    moment(date).format(
      moment(date).isSame(moment(), "year") ? "D MMM" : "D MMM YYYY",
    );
  const movedDatesNotice =
    isDuplicate &&
    sourceStartDate &&
    sourceDueDate &&
    !moment(sourceStartDate).isSame(moment(), "day")
      ? `Dates moved from ${formatSourceDate(sourceStartDate)}–${formatSourceDate(
          sourceDueDate,
        )}. Check them before you publish.`
      : null;

  return (
    <Layout>
      <ProgramFormLeaveDialog
        open={pendingLeave !== null}
        nounLower={nounLower}
        saveLabel={isDraftProgram ? "Save draft" : "Save changes"}
        onSave={handleSaveAndLeave}
        onDiscard={handleDiscardAndLeave}
        onCancel={() => setPendingLeave(null)}
      />
      <ConfirmKickoffDialog />
      <Layout.Header
        breadcrumbs={[
          { title: rootCrumb.title, url: rootCrumb.url },
          {
            title: `${isNew ? "New" : isDuplicate ? "Duplicate" : "Edit"} ${
              complianceProgramType === ComplianceProgramType.ENGAGEMENT_SURVEY
                ? "Survey"
                : label("review", { capitalize: true })
            }`,
            url: location.pathname,
          },
        ]}
      />
      <Layout.Container>
        <main className="absolute inset-0">
          <div className="@container/layout-main mx-auto">
            {isLoading ? (
              <div className="mt-20 flex items-center justify-center h-full w-full">
                <Loading>Loading {nounLower}...</Loading>
              </div>
            ) : (
              <>
                {/* The page's colour, with the line under the tabs running the
                    full width rather than stopping at the padding. */}
                <div className="z-30 px-8 pt-4 sticky top-0 border-b bg-content-background">
                  <Tabs
                    className="border-b-0"
                    options={[
                      {
                        value: ProgramFormTab.Workflow,
                        label: "Workflow",
                        hasError:
                          showTabErrorMarkers &&
                          tabsWithErrors.has(ProgramFormTab.Workflow),
                        ...lockedTab(ProgramFormTab.Workflow),
                        stepNumber: stepNumber(ProgramFormTab.Workflow),
                        isComplete:
                          isGuidedFlow &&
                          doneSteps.has(ProgramFormTab.Workflow),
                      },
                      {
                        value: ProgramFormTab.Questions,
                        label: "Questions",
                        hasError:
                          showTabErrorMarkers &&
                          tabsWithErrors.has(ProgramFormTab.Questions),
                        ...lockedTab(ProgramFormTab.Questions),
                        stepNumber: stepNumber(ProgramFormTab.Questions),
                        isComplete:
                          isGuidedFlow &&
                          doneSteps.has(ProgramFormTab.Questions),
                      },
                      {
                        value: ProgramFormTab.Participants,
                        label: "Participants",
                        hasError:
                          showTabErrorMarkers &&
                          tabsWithErrors.has(ProgramFormTab.Participants),
                        ...lockedTab(ProgramFormTab.Participants),
                        stepNumber: stepNumber(ProgramFormTab.Participants),
                        isComplete:
                          isGuidedFlow &&
                          doneSteps.has(ProgramFormTab.Participants),
                      },
                      {
                        value: ProgramFormTab.Notifications,
                        label: "Notifications",
                        hasError:
                          showTabErrorMarkers &&
                          tabsWithErrors.has(ProgramFormTab.Notifications),
                        ...lockedTab(ProgramFormTab.Notifications),
                        stepNumber: stepNumber(ProgramFormTab.Notifications),
                        isComplete:
                          isGuidedFlow &&
                          doneSteps.has(ProgramFormTab.Notifications),
                      },
                      // No error marker: nothing on this tab can stop a save.
                      // For a draft it is where the steps end rather than a
                      // step of its own, so it has an icon, not a number, and
                      // is never marked done.
                      {
                        value: ProgramFormTab.Readiness,
                        label: isGuidedFlow ? "Review & publish" : "Readiness",
                        ...lockedTab(ProgramFormTab.Readiness),
                        stepIcon: isGuidedFlow ? "eye" : undefined,
                      },
                    ]}
                    selectedValue={selectedTab}
                    onClickOptionValue={handleClickTab}
                  />
                </div>
                {/* On a wide Questions tab the page stops scrolling and each
                    of its two columns scrolls on its own instead. The bottom
                    padding keeps the columns clear of the step bar. */}
                <div
                  ref={scrollContainerRef}
                  className={classNames(
                    "absolute inset-0 overflow-y-scroll pt-20 pb-32",
                    selectedTab === ProgramFormTab.Questions &&
                      "@[1200px]/layout-main:overflow-hidden @[1200px]/layout-main:pb-16",
                  )}
                >
                  <div
                    className={classNames(
                      "mx-auto px-8 py-6",
                      selectedTab === ProgramFormTab.Questions
                        ? // Two 768 columns + 64 gap + 64 padding, so the
                          // preview can sit beside the configuration.
                          "max-w-[1664px] @[1200px]/layout-main:flex @[1200px]/layout-main:h-full @[1200px]/layout-main:flex-col"
                        : "max-w-(--breakpoint-md)",
                    )}
                  >
                    {movedDatesNotice && (
                      <Callout
                        theme="warning"
                        icon="info"
                        className="mb-6"
                        size="small"
                      >
                        {movedDatesNotice}
                      </Callout>
                    )}
                    {visibleErrors.length > 0 && (
                      <ProgramFormErrorBanner
                        errors={visibleErrors}
                        action={submitAttempted ? "saving" : "continuing"}
                        fieldToTab={fieldToTab}
                        onJumpToError={handleJumpToError}
                      />
                    )}
                    {selectedTab === ProgramFormTab.Workflow && (
                      <ProgramFormWorkflow
                        complianceProgramType={complianceProgramType}
                        proposedComplianceProgram={proposedComplianceProgram}
                        onUpdateProposedComplianceProgram={
                          handlePartialProgramFormUpdate
                        }
                        canUpdate={canUpdateTemplates}
                        onAddOrUpdateStep={handleAddOrUpdateStep}
                        onRemoveStep={handleRemoveStep}
                        errors={visibleErrors}
                      />
                    )}
                    {selectedTab === ProgramFormTab.Questions && (
                      <ProgramFormQuestions
                        proposedComplianceProgram={proposedComplianceProgram}
                        canUpdate={canUpdateTemplates}
                        publishedReviewId={publishedReviewId}
                        talentIndicatorData={
                          complianceProgramData
                            ? assertEdgesNonNull(
                                complianceProgramData?.talentIndicators,
                              )
                            : []
                        }
                        onAddOrUpdateStep={handleAddOrUpdateStep}
                        availableAssessmentGroups={availableAssessmentGroups}
                        onAssessmentGroupCreated={handleAssessmentGroupCreated}
                        onAssessmentGroupArchived={
                          handleAssessmentGroupArchived
                        }
                        errors={visibleErrors}
                      />
                    )}
                    {selectedTab === ProgramFormTab.Participants && (
                      <ProgramFormParticipants
                        creator={
                          complianceProgramData?.complianceProgram?.creator ??
                          currentUser
                        }
                        proposedComplianceProgram={proposedComplianceProgram}
                        onUpdateProposedComplianceProgram={
                          handlePartialProgramFormUpdate
                        }
                        canUpdate={canUpdateTemplates}
                        errors={visibleErrors}
                      />
                    )}
                    {selectedTab === ProgramFormTab.Notifications && (
                      <ProgramFormNotifications
                        proposedComplianceProgram={proposedComplianceProgram}
                        onUpdateProposedComplianceProgram={
                          handlePartialProgramFormUpdate
                        }
                        canUpdate={canUpdateTemplates}
                      />
                    )}
                    {selectedTab === ProgramFormTab.Readiness && (
                      <div className="space-y-12">
                        <section>
                          <div className="text-lg font-semibold">Summary</div>
                          <div className="mt-3">
                            <ProgramFormSummary
                              proposedComplianceProgram={
                                proposedComplianceProgram
                              }
                              creator={
                                complianceProgramData?.complianceProgram
                                  ?.creator ?? currentUser
                              }
                            />
                          </div>
                          <div className="mt-4 empty:hidden">
                            <ProgramFormWhoCanStepIn
                              proposedComplianceProgram={
                                proposedComplianceProgram
                              }
                            />
                          </div>
                        </section>
                        <ProgramFormReadiness
                          proposedComplianceProgram={proposedComplianceProgram}
                        />
                      </div>
                    )}
                  </div>
                </div>
                {/* Every action lives in the bar under the form, for a draft
                    and a published review alike, so the top stays empty. */}
                {isGuidedFlow ? (
                  <ProgramFormStepBar
                    back={backButton}
                    forward={
                      <>
                        {saveDraftButton}
                        {forwardButtons}
                      </>
                    }
                  />
                ) : (
                  <ProgramFormStepBar
                    back={discardButton}
                    forward={saveChangesButton}
                  />
                )}
              </>
            )}
          </div>
        </main>
      </Layout.Container>
    </Layout>
  );
};

export default ComplianceProgramForm;
